import { useState, useMemo, FormEvent } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import RateDeckImport from "./components/RateDeckImport";
import { coverageDestinations, regions, faqItems } from "@/mocks/coverage";

const regionFilterOptions = ["All", "North America", "Latin America", "Europe", "MENA", "Asia Pacific", "Africa"];
const tierFilterOptions = ["All tiers", "Premium", "Standard"];
const cliFilterOptions = ["All", "CLI only"];

const regionColor: Record<string, { badge: string; ring: string }> = {
  "North America": { badge: "bg-primary-100 text-primary-800", ring: "ring-primary-400" },
  "Latin America": { badge: "bg-accent-100 text-accent-800", ring: "ring-accent-400" },
  "Europe": { badge: "bg-secondary-100 text-secondary-800", ring: "ring-secondary-400" },
  "MENA": { badge: "bg-primary-100 text-primary-800", ring: "ring-primary-400" },
  "Asia Pacific": { badge: "bg-accent-100 text-accent-800", ring: "ring-accent-400" },
  "Africa": { badge: "bg-secondary-100 text-secondary-800", ring: "ring-secondary-400" },
};

export default function CoverageRates() {
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("All");
  const [tierFilter, setTierFilter] = useState("All tiers");
  const [cliFilter, setCliFilter] = useState("All");

  // Quote form state
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const filtered = useMemo(() => {
    return coverageDestinations.filter((d) => {
      const q = search.toLowerCase();
      const matchesSearch =
        d.country.toLowerCase().includes(q) ||
        d.prefix.includes(q) ||
        d.region.toLowerCase().includes(q);
      const matchesRegion = regionFilter === "All" || d.region === regionFilter;
      const matchesTier = tierFilter === "All tiers" || d.routeTier === tierFilter;
      const matchesCli = cliFilter === "All" || d.cli === "Yes";
      return matchesSearch && matchesRegion && matchesTier && matchesCli;
    });
  }, [search, regionFilter, tierFilter, cliFilter]);

  const handleQuoteSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const hp = ((fd.get("company_alt") as string) || "").trim();
    if (hp) {
      setFormStatus("success");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    const message = (fd.get("message") as string) || "";
    if (message.length > 500) {
      setFormError("Please keep your message under 500 characters.");
      setFormStatus("error");
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => body.append(key, value as string));
      const res = await fetch("https://readdy.ai/api/form/d9tqa0l3pcjqs2fcshtg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || text || "";
      if (res.ok && parsed?.code === "OK" && !serverMsg.toLowerCase().includes("spam")) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "Something went wrong. Please try again.");
        setFormStatus("error");
      }
    } catch {
      setFormError("Network error. Please try again.");
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Coverage & Rates</span>
            </nav>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-5">
              1000+ destinations · Real-time quality data
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              Global coverage. Transparent rates.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              Search our 1000+ destination coverage, request a live rate deck, or get a custom LCR quote tailored to your traffic profile.
            </p>
          </div>
        </section>

        {/* Search bar */}
        <section className="pb-10 md:pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-stretch gap-2 bg-background-50 border border-background-200 rounded-xl p-2">
              <div className="flex-1 flex items-center gap-2 px-3">
                <i className="ri-search-line text-foreground-500 w-5 h-5 flex items-center justify-center" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country, prefix, or region (e.g. Brazil Mobile, +44)"
                  className="w-full text-sm bg-transparent outline-none py-3 text-foreground-950 placeholder-foreground-500"
                />
              </div>
              <div className="flex items-center gap-2 px-1">
                <select
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  className="text-sm bg-background-100 border border-background-200 rounded-md px-3 py-2.5 outline-none focus:border-primary-400 text-foreground-950 cursor-pointer"
                >
                  {regionFilterOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="text-sm bg-background-100 border border-background-200 rounded-md px-3 py-2.5 outline-none focus:border-primary-400 text-foreground-950 cursor-pointer"
                >
                  {tierFilterOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select
                  value={cliFilter}
                  onChange={(e) => setCliFilter(e.target.value)}
                  className="text-sm bg-background-100 border border-background-200 rounded-md px-3 py-2.5 outline-none focus:border-primary-400 text-foreground-950 cursor-pointer"
                >
                  {cliFilterOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => { setSearch(""); setRegionFilter("All"); setTierFilter("All tiers"); setCliFilter("All"); }}
                className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2.5 whitespace-nowrap cursor-pointer"
              >
                Clear
              </button>
            </div>
          </div>
        </section>

        {/* Results table */}
        <section className="pb-10 md:pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">
                <span className="font-semibold text-foreground-950">{filtered.length}</span> destinations matching your search
              </p>
              <div className="flex items-center gap-2">
                <button type="button" className="text-xs px-3 py-1.5 rounded-md border border-background-200 text-foreground-700 hover:border-primary-400 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-download-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Export CSV
                </button>
                <button type="button" className="text-xs px-3 py-1.5 rounded-md border border-background-200 text-foreground-700 hover:border-primary-400 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-download-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Export XLSX
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200 bg-background-50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wider text-foreground-500 bg-background-100/60">
                    <th className="px-4 py-3 font-medium">Country</th>
                    <th className="px-4 py-3 font-medium">Region</th>
                    <th className="px-4 py-3 font-medium">Prefix</th>
                    <th className="px-4 py-3 font-medium">Mobile ASR</th>
                    <th className="px-4 py-3 font-medium">Mobile PDD</th>
                    <th className="px-4 py-3 font-medium">Fixed ASR</th>
                    <th className="px-4 py-3 font-medium">Tier</th>
                    <th className="px-4 py-3 font-medium">CLI</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => {
                    const rc = regionColor[d.region] || { badge: "bg-background-100 text-foreground-700", ring: "ring-background-300" };
                    return (
                      <tr key={d.country} className="border-b border-background-200 last:border-0 hover:bg-background-100/40 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground-950">{d.country}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${rc.badge}`}>{d.region}</span>
                        </td>
                        <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{d.prefix}</td>
                        <td className="px-4 py-3 text-foreground-700">{d.mobileAsr}</td>
                        <td className="px-4 py-3 text-foreground-600">{d.mobilePdd}</td>
                        <td className="px-4 py-3 text-foreground-700">{d.fixedAsr}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                            d.routeTier === "Premium" ? "bg-primary-100 text-primary-800" : "bg-accent-100 text-accent-800"
                          }`}>
                            {d.routeTier}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[11px] px-2 py-1 rounded-full bg-secondary-100 text-secondary-800 font-medium">{d.cli}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-accent-700 font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                            {d.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Regions */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Regions</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Six regions. One platform.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {regions.map((r) => {
                const rc = regionColor[r.name] || { badge: "bg-background-100 text-foreground-700", ring: "ring-background-300" };
                return (
                  <div key={r.name} className={`rounded-xl border border-background-200 bg-background-50 p-6 hover:ring-1 ${rc.ring} transition-all cursor-pointer`}>
                    <div className="flex items-start justify-between">
                      <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                        <i className="ri-map-pin-2-line text-xl w-6 h-6 flex items-center justify-center" />
                      </span>
                      <span className="text-xs font-semibold text-accent-700">{r.count}+ dest.</span>
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-foreground-950 font-heading">{r.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{r.prefix}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Rate deck CTA */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Rate decks</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                  Download a live rate deck.
                </h2>
                <p className="mt-4 text-foreground-700 leading-relaxed max-w-xl">
                  Our rate decks are updated weekly and include per-destination pricing for both Premium and Standard tiers. Choose your preferred format — CSV, XLSX, or JSON — and import directly into your switch or LCR engine.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button type="button" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                    <i className="ri-file-download-line w-4 h-4 flex items-center justify-center" />
                    Download CSV
                  </button>
                  <button type="button" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-5 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                    <i className="ri-file-excel-line w-4 h-4 flex items-center justify-center" />
                    Download XLSX
                  </button>
                  <button type="button" className="inline-flex items-center gap-2 bg-background-50 border border-background-300 hover:border-foreground-400 text-foreground-800 px-5 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                    <i className="ri-code-box-line w-4 h-4 flex items-center justify-center" />
                    API endpoint
                  </button>
                </div>
                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 flex items-start gap-3">
                  <i className="ri-information-line text-accent-600 w-5 h-5 flex items-center justify-center mt-0.5" />
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Rate decks include per-destination per-minute rates for Mobile and Fixed lines, CLI availability, minimum ASR targets, and route tier assignments. Updated every Monday at 00:00 UTC. Current deck date: <strong className="text-foreground-800">August 11, 2026</strong>.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                      <i className="ri-file-list-line text-lg w-5 h-5 flex items-center justify-center" />
                    </span>
                    <h3 className="text-base font-semibold text-foreground-950">Latest rate deck snapshot</h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    {[
                      { label: "Destinations covered", value: "1000+" },
                      { label: "Format", value: "CSV / XLSX / JSON" },
                      { label: "Update frequency", value: "Weekly (Mondays)" },
                      { label: "Tiers included", value: "Premium + Standard" },
                      { label: "Mobile / Fixed split", value: "Yes" },
                      { label: "CLI flag", value: "Yes" },
                      { label: "Next update", value: "Aug 18, 2026" },
                    ].map((row) => (
                      <div key={row.label} className="flex items-center justify-between border-b border-background-200 last:border-0 pb-2 last:pb-0">
                        <span className="text-foreground-600">{row.label}</span>
                        <span className="font-medium text-foreground-950">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Custom quote form */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Custom quote</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                  Get a tailored LCR quote.
                </h2>
                <p className="mt-4 text-foreground-700 leading-relaxed">
                  Share your top 10 destinations and monthly volume. We&apos;ll build a targeted rate deck and suggest a routing profile optimized for your traffic mix.
                </p>
                <div className="mt-6 space-y-3">
                  {[
                    "Targeted pricing for your top destinations",
                    "Route tier recommendations per country",
                    "Volume-based rate discounts",
                    "Delivery within one business day",
                  ].map((b) => (
                    <div key={b} className="flex items-center gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-accent-600 w-4 h-4 flex items-center justify-center" />
                      {b}
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-7">
                <form
                  onSubmit={handleQuoteSubmit}
                  data-readdy-form
                  id="voxmaster-coverage-quote"
                  className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">Full name *</label>
                      <input required type="text" name="name" className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">Work email *</label>
                      <input required type="email" name="email" className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950" placeholder="jane@company.com" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">Company *</label>
                      <input required type="text" name="company" className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950" placeholder="Acme Telecom" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">Monthly minute volume</label>
                      <select name="volume" defaultValue="" className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950 cursor-pointer">
                        <option value="" disabled>Select volume</option>
                        <option>Under 100k</option>
                        <option>100k – 1M</option>
                        <option>1M – 10M</option>
                        <option>10M – 100M</option>
                        <option>100M+</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Top destinations (top 3-10)</label>
                    <input type="text" name="destinations" className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950" placeholder="e.g. USA Mobile, UK Mobile, Nigeria Mobile, India Mobile" />
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Services needed</label>
                    <div className="flex flex-wrap gap-2">
                      {["Voice Termination", "DID / Origination", "SIP Trunking", "All of the above"].map((svc) => (
                        <label key={svc} className="text-xs px-3 py-1.5 rounded-full border border-background-300 text-foreground-700 cursor-pointer whitespace-nowrap hover:border-primary-400 has-[:checked]:bg-primary-100 has-[:checked]:border-primary-500 has-[:checked]:text-primary-900">
                          <input type="checkbox" name="services" value={svc} className="hidden" />
                          {svc}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Notes (optional, 500 chars)</label>
                    <textarea name="message" maxLength={500} rows={3} className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950 resize-none" placeholder="Current provider, pain points, integration timeline..." />
                  </div>

                  {/* Honeypot */}
                  <div className="vx-hp-shield" aria-hidden="true">
                    <label>
                      Alternate company
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </label>
                  </div>

                  <div className="mt-6 flex items-center justify-between gap-4">
                    <p className="text-xs text-foreground-500">We respond within one business day.</p>
                    <button type="submit" disabled={formStatus === "submitting"} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 px-6 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                      {formStatus === "submitting" ? "Sending…" : "Get custom quote"}
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                    </button>
                  </div>

                  {formStatus === "success" && (
                    <div className="mt-5 rounded-md border border-accent-300 bg-accent-100 text-accent-900 text-sm px-4 py-3 flex items-start gap-2">
                      <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center mt-0.5" />
                      Quote request received — we&apos;ll get back within one business day with a tailored rate deck.
                    </div>
                  )}
                  {formStatus === "error" && (
                    <div className="mt-5 rounded-md border border-primary-300 bg-primary-100 text-primary-900 text-sm px-4 py-3 flex items-start gap-2">
                      <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center mt-0.5" />
                      {formError || "Something went wrong. Please try again."}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Import rate deck */}
        <RateDeckImport />
        {/* FAQ */}
        <section id="faq" className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">FAQ</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                  Frequently asked questions.
                </h2>
                <p className="mt-3 text-foreground-700 leading-relaxed">
                  The answers our carrier-relations team gives most often about rates, routes, and coverage.
                </p>
              </div>

              <div className="lg:col-span-8 space-y-3">
                {faqItems.map((faq, i) => (
                  <div key={i} className="rounded-xl border border-background-200 bg-background-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
                    >
                      <span className="text-sm font-semibold text-foreground-950 pr-4">{faq.q}</span>
                      <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-foreground-500 w-5 h-5 flex items-center justify-center shrink-0 transition-transform`} />
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4 pt-0 text-sm text-foreground-600 leading-relaxed border-t border-background-200">
                        <div className="pt-4">{faq.a}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}